import React from 'react';
import { Box, IconButton, TableCell, TableRow } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import { CustomSelect } from 'components/FormFields/CustomSelect/CustomSelect';
import { CustomMenu } from 'components/CustomMenu/CustomMenu';
import { PrizeType, PrizeTypeLabel, type IPrizeUpload } from 'types/IPrize';
import { EMPTY_AMOUNT } from 'utility/amountInput';
import { DrawingBasePrizeDetailsDialog } from './DrawingBasePrizeDetailsDialog';

/** One row of the prize table: a saved prize or a line the editor just added. */
export interface PrizeRow {
  /** Stable key for React; a new row has no id yet. */
  key: string;
  id?: number;
  /** German amount with two decimals, e.g. "5000,00". */
  amount: string;
  typ: PrizeType;
  title: string;
  description: string;
  uploads: IPrizeUpload[];
  pendingFiles: File[];
}

const prizeTypeOptions = Object.entries(PrizeTypeLabel).map(([id, title]) => ({ id, title }));

interface IProps {
  row: PrizeRow;
  isLast: boolean;
  amountError?: string;
  onChange: (row: PrizeRow) => void;
  onAdd: () => void;
  onRemove: () => void;
  onUpload: (file: File) => void;
  onRemoveUpload: (upload: IPrizeUpload) => void;
  onRemovePendingFile: (index: number) => void;
  onDragStart: () => void;
  onDropOn: () => void;
}

const isFormField = (target: EventTarget | null) => target instanceof HTMLElement && Boolean(target.closest('input, textarea, select, button, a, [role="combobox"]'));

export const DrawingBasePrizeRow: React.FC<IProps> = ({ row, isLast, amountError, onChange, onAdd, onRemove, onUpload, onRemoveUpload, onRemovePendingFile, onDragStart, onDropOn }) => {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  // Firefox blocks clicking and selecting text in inputs inside a draggable element,
  // so the row is draggable only while the mouse is pressed outside a form field.
  const [dragEnabled, setDragEnabled] = React.useState(false);
  const isMaterial = row.typ === PrizeType.SACHGEWINN;

  return (
    <>
      <TableRow
        className={`psf-styled-table__body-row ${isLast ? 'psf-styled-table__body-last-row' : ''}`}
        draggable={dragEnabled}
        onMouseDown={(event) => setDragEnabled(!isFormField(event.target))}
        onMouseUp={() => setDragEnabled(false)}
        onDragEnd={() => setDragEnabled(false)}
        onDragStart={(event) => {
          if (isFormField(event.target)) {
            event.preventDefault();
            return;
          }
          // Firefox starts a drag only when data is set.
          event.dataTransfer.setData('text/plain', row.key);
          event.dataTransfer.effectAllowed = 'move';
          onDragStart();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
        }}
        onDrop={(event) => {
          event.preventDefault();
          onDropOn();
        }}
        sx={{ cursor: 'move' }}
      >
        <TableCell>
          <CustomTextField
            name={`prize-amount-${row.key}`}
            value={row.amount}
            // Digits and one comma, as in the old form.
            onChange={(event) => onChange({ ...row, amount: event.target.value.replace(/[^0-9,]/g, '') })}
            onFocus={(event) => {
              if (event.target.value === EMPTY_AMOUNT) event.target.select();
            }}
            error={Boolean(amountError)}
            helperText={amountError ?? ''}
          />
        </TableCell>

        <TableCell>
          <CustomSelect
            name={`prize-type-${row.key}`}
            options={prizeTypeOptions}
            value={String(row.typ)}
            onChange={(value) => {
              const typ = Number(value) as PrizeType;
              onChange({ ...row, typ });
              if (typ === PrizeType.SACHGEWINN && !isMaterial) setDetailsOpen(true);
            }}
          />
        </TableCell>

        <TableCell>
          <Box className="psf-styled-table__body-cell-action-container">
            <IconButton className="psf-styled-table__body-cell-action-button" aria-label="Aktionen" onClick={(event) => setMenuAnchor(event.currentTarget)}>
              <MoreVertIcon className="psf-styled-table__body-cell-action-button-icon" />
            </IconButton>
          </Box>

          <CustomMenu
            anchorEl={menuAnchor}
            handleMenuClose={() => setMenuAnchor(null)}
            withDivider
            isArrowRight
            items={[
              ...(isMaterial ? [{ name: 'Sachgewinn bearbeiten', onClick: () => setDetailsOpen(true) }] : []),
              { name: 'Gewinn hinzufügen', onClick: onAdd },
              { name: 'Gewinn löschen', onClick: onRemove },
            ]}
          />
        </TableCell>
      </TableRow>

      {/* Outside the row, so events in the dialog do not reach the row's drag handlers. */}
      {isMaterial && (
        <DrawingBasePrizeDetailsDialog
          open={detailsOpen}
          row={row}
          onClose={() => setDetailsOpen(false)}
          onChange={onChange}
          onUpload={onUpload}
          onRemoveUpload={onRemoveUpload}
          onRemovePendingFile={onRemovePendingFile}
        />
      )}
    </>
  );
};
